import React from 'react';
import './sectionSkeleton.css';

/**
 * A section that is still loading, shown AS THAT SECTION.
 *
 * The booking page builds itself from several independent fetches, and each of
 * those sections rendered `null` until its own request landed — so the page
 * settled, looked finished, and then blocks of it appeared out of nowhere
 * seconds later ("loads extremely weirdly with some section loading much later
 * not even showing clearly that they are loading", 2026-08-30).
 *
 * Holding the space with a titled placeholder makes the page honest: what is
 * coming, and that it has not arrived yet. A section with genuinely nothing to
 * show still renders nothing — but only once it KNOWS that.
 *
 * @param {string} title    the heading the real section will use
 * @param {number} rows     how many placeholder lines / table rows / grid rows
 * @param {string} variant  'rows' (text lines) | 'grid' (label/value tiles)
 *                          | 'table' (header + rows) | 'tiles' (big stat tiles)
 * @param {number} cols     columns for 'grid' / 'table' (default 3 / 5)
 * @param {number} tiles    stat tiles for 'tiles' (default 2)
 *
 * The variants exist because a wall of identical grey text lines says
 * "something loads here" but not WHAT (owner, 2026-09-03) — a users table
 * should look like a table before it arrives, a wallet like its tiles.
 */
export default function SectionSkeleton({ title, rows = 3, variant = 'rows', cols, tiles = 2 }) {
  return (
    <section className="dp-card booking-section sksec" aria-busy="true" aria-live="polite">
      <h3 className="sksec__title">
        {title}
        <span className="sksec__hint">loading…</span>
      </h3>
      <SkeletonBody rows={rows} variant={variant} cols={cols} tiles={tiles} />
    </section>
  );
}

export function SkeletonBody({ rows = 3, variant = 'rows', cols, tiles = 2 }) {
  if (variant === 'grid') {
    const c = cols || 3;
    return (
      <div className="sksec__grid" aria-busy="true" style={{ '--sk-cols': c }}>
        {Array.from({ length: rows * c }, (_, i) => (
          <span key={i} className="sksec__cell">
            <span className="sksec__row sksec__row--label" style={{ width: `${45 + (i % 3) * 12}%` }} />
            <span className="sksec__row" style={{ width: `${70 + ((i * 7) % 25)}%` }} />
          </span>
        ))}
      </div>
    );
  }
  if (variant === 'table') {
    const c = cols || 5;
    return (
      <div className="sksec__table" aria-busy="true" style={{ '--sk-cols': c }}>
        <div className="sksec__tr sksec__tr--head">
          {Array.from({ length: c }, (_, i) => (
            <span key={i} className="sksec__row sksec__row--label" style={{ width: `${50 + (i % 2) * 20}%` }} />
          ))}
        </div>
        {Array.from({ length: rows }, (_, r) => (
          <div key={r} className="sksec__tr">
            {Array.from({ length: c }, (_, i) => (
              <span key={i} className="sksec__row" style={{ width: `${60 + ((r + i) % 3) * 15}%` }} />
            ))}
          </div>
        ))}
      </div>
    );
  }
  if (variant === 'tiles') {
    return (
      <div aria-busy="true">
        <div className="sksec__tiles">
          {Array.from({ length: tiles }, (_, i) => (
            <span key={i} className="sksec__tile">
              <span className="sksec__row sksec__row--label" style={{ width: '55%' }} />
              <span className="sksec__row sksec__row--big" style={{ width: '70%' }} />
            </span>
          ))}
        </div>
        {rows > 0 && (
          <div className="sksec__rows">
            {Array.from({ length: rows }, (_, i) => (
              <span key={i} className="sksec__row" style={{ width: `${100 - i * 12}%` }} />
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="sksec__rows" aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        // Descending widths read as text rather than as blocks.
        <span key={i} className="sksec__row" style={{ width: `${100 - i * 12}%` }} />
      ))}
    </div>
  );
}

/* The rows alone, for a card that already exists (Reports' initial fetch). */
export function SkeletonRows({ rows = 3 }) {
  return (
    <div className="sksec__rows" aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <span key={i} className="sksec__row" style={{ width: `${100 - i * 12}%` }} />
      ))}
    </div>
  );
}
